/**
 * BrandDetailPresenter
 * --
 */
import React, { useState } from 'react';
import {
  Content,
  Row,
  Col,
  Text,
  Collapse02,
  DropDownMenu,
  ProductItem,
  Pagination,
  FilterOption,
  Button,
  ModalLayout,
} from 'components';
import { encrypt } from 'utils/Crypto';
import { Badge, Empty, Image, Spin } from 'antd';
import Cart from 'assets/icon/cart_icon.png';
import Heart from 'assets/icon/heart_icon.png';
import { ProductCartModal } from '../Products/components';
import { HeartFilled } from '@ant-design/icons';
import Filter from 'assets/icon/filter_icon.svg';

const PAGE_NUM = [
  { label: '12개씩', value: 12 },
  { label: '24개씩', value: 24 },
  { label: '36개씩', value: 36 },
  { label: '48개씩', value: 48 },
];

const BrandDetailPresenter = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const {
    navigate,
    location,
    brand,
    products,
    categories,
    selectedCategory,
    modalData,
    filterItems,
    cartList,
    setModalData,
    user,
    onSubmit,
    sortItems,
    screenSize,
  } = props;

  /* ===== STATE ===== */
  // 정렬키값
  const [tabKey, setTabKey] = React.useState(sortItems[0].key);
  // 페이지에 보여질 수
  const [pageSize, setPageSize] = React.useState(PAGE_NUM[0].value);
  // 선택된 필터 값
  const [selectedFilter, setSelectedFilter] = React.useState([]);
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 상품 목록
  const [productItems, setProductItems] = React.useState([]);
  // 페이징
  const [currentPage, setCurrentPage] = React.useState(1);
  // 모바일 필터 모달
  const [isOpen, setIsOpen] = useState(false);

  /* ===== VARIABLE ===== */
  const styles = {
    background: {
      position: 'relative',
      width: '100%',
      height: '100%',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    },
    overlay: {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    },
    backgroundInfo: {
      position: 'absolute',
      top: 0,
      left: '50%',
      transform: 'translateX(-50%)',
      maxWidth: 1500,
      width: screenSize >= 768 ? 'calc(100% - 100px)' : '100%',
      height: '100%',
      display: 'flex',
      gap: 30,
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'start',
      padding: screenSize >= 768 ? 0 : 20,
    },

    button: {
      width: '100%',
      height: screenSize >= 500 ? 48 : 40,
      fontSize: screenSize >= 500 ? 16 : 14,
      fontWeight: 700,
      borderRadius: 0,
    },

    categoryWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      marginBottom: 12,
    },
    categoryTitle: {
      fontSize: screenSize >= 500 ? 16 : 15,
      fontWeight: 700,
      lineHeight: '28px',
      cursor: 'pointer',
    },
    categoryItem: {
      fontSize: screenSize >= 500 ? 16 : 14,
      fontWeight: 400,
      lineHeight: '28px',
      color: '#666666',
      border: 'none',
      flex: 1,
      cursor: 'pointer',
    },
  };

  /* ===== FUNCTION ===== */
  /**
   * 카테고리 라우팅
   * --
   * @param {number} category
   */
  const handleCategoryRoute = (category) => {
    // 기존 쿼리 파라미터 가져오기
    const currentParams = new URLSearchParams(location.search);

    // 카테고리 쿼리 추가
    currentParams.set('category', encodeURIComponent(encrypt(category)));

    // 쿼리와 함께 새로운 URL로 이동
    navigate(`${location.pathname}?${currentParams.toString()}`, {
      replace: true,
    });
  };

  /**
   * 카테고리 Tree 렌더
   * --
   * @param {object} items
   * @returns
   */
  const renderChild = (items) => {
    const { children, title, key } = items;

    const splitKey = key.split('-');
    const depth = splitKey.length;
    const paddingNum = depth - 1 > 3 ? 3 : depth - 1;

    return children.length ? (
      <Collapse02
        key={key}
        title={title}
        titleStyle={{
          ...styles.categoryItem,
          paddingLeft: 24 * paddingNum,
          ...(selectedCategory?.product_category_id.toString() ===
            splitKey[depth - 1] && {
            color: '#D33444',
            fontWeight: 700,
          }),
        }}
        children={children.map((child) => renderChild(child))}
        //defaultOpened={true}
        collapsible={'icon'}
        onClick={() => handleCategoryRoute(splitKey[depth - 1])}
        iconColor={'#666666'}
      />
    ) : (
      <p
        key={key}
        style={{
          ...styles.categoryItem,
          paddingLeft: 24 * paddingNum,
          ...(selectedCategory?.product_category_id.toString() ===
            splitKey[depth - 1] && {
            color: '#D33444',
            fontWeight: 700,
          }),
        }}
        onClick={() => handleCategoryRoute(splitKey[depth - 1])}
      >
        {title}
      </p>
    );
  };

  /**
   * 장바구니/위시리스트 모달
   * --
   */
  const handleRenderModal = (t, d = null) => {
    if (!user) {
      const confirm = window.confirm(
        '로그인 후 이용가능합니다.\n로그인하시겠습니까?'
      );

      if (confirm) {
        navigate('signin', {
          state: {
            from: `${location.pathname}${
              location.search ? location.search : ''
            }`,
          },
        });
      } else {
        return;
      }
    }

    if (t === 'wishlist' && d.isWish) {
      const confirm = window.confirm('찜 목록에서 삭제하시겠습니까?');

      if (confirm) {
        return onSubmit('remove', d);
      } else {
        return;
      }
    }

    if (!d) return setModalData({ type: '', data: null });
    else setModalData({ type: t, data: d });
  };

  /**
   * 필터 핸들러
   * --
   */
  const handleSelectFilter = React.useCallback((key, value, type) => {
    setSelectedFilter((prev) => {
      const fid = prev.findIndex((select) => select.key === key);
      if (fid === -1) {
        return [...prev, { key, value, type }];
      }
      const existingFilter = prev[fid];
      // 상태가 동일하면 업데이트 생략
      if (existingFilter.value === value && existingFilter.type === type) {
        return prev;
      }
      // 기존 필터 업데이트
      const updatedFilter = [...prev];
      updatedFilter[fid] = { key, value, type };
      return updatedFilter;
    });
  }, []);

  /**
   * 필터 검색
   * --
   */
  const handleSearch = (type) => {
    const searchType = type.toLocaleUpperCase();

    let filterProduct = [];
    setIsLoading(true);

    switch (searchType) {
      case 'RESET':
        filterProduct = productList;
        setSelectedFilter([]);
        break;
      case 'SEARCH':
        const filterItems = selectedFilter.filter(
          (select) => select.value && select.value.length
        );
        const filterList = productList.filter((product) =>
          filterItems.every(({ key, type, value }) => {
            switch (type) {
              case 'include':
                // value 배열에 product[key]가 포함되는지 확인
                return value.includes(product[key]);
              case 'range':
                // value 배열의 범위 중 하나라도 product[key]가 포함되면 true
                return value.some(
                  ([min, max]) => product[key] >= min && product[key] < max
                );
              default:
                // 알 수 없는 type은 기본적으로 true
                return true;
            }
          })
        );
        filterProduct = filterList;
        break;
      default:
        filterProduct = productList;
        break;
    }

    setTimeout(() => {
      setProductItems(filterProduct);
      setIsLoading(false);
    }, 500);
  };

  /* ===== HOOKS ===== */
  /**
   * 카테고리에 따른 상품 정제
   * --
   */
  const productList = React.useMemo(() => {
    if (!selectedCategory) return products;

    const { product_category_id } = selectedCategory;

    // NOTE: 카테고리에 따른 아이템 필터
    const filterList = products.filter((product) => {
      const { category } = product;

      const splitKey = category?.split('-');

      return splitKey?.some((sk) => product_category_id.toString() === sk);
    });

    setSelectedFilter([]);
    return filterList;
  }, [products, selectedCategory]);

  /**
   * 상품 목록 초기화
   * --
   */
  React.useEffect(() => {
    if (!productList.length) return;

    setProductItems(productList);
  }, [productList]);

  /**
   * 정렬
   * --
   */
  React.useEffect(() => {
    // 정렬
    const tapSplit = tabKey.split('-');
    const sortData = [...productItems]?.sort((a, b) => {
      switch (tapSplit[0]) {
        case 'ASC':
          return a[tapSplit?.[1]] - b[tapSplit?.[1]];
        case 'DESC':
          return b[tapSplit?.[1]] - a[tapSplit?.[1]];
        default:
          return 0;
      }
    });

    setProductItems(sortData);
    // eslint-disable-next-line
  }, [tabKey]);

  /**
   * 정렬값 초기화
   * --
   */
  React.useEffect(() => {
    setTabKey(sortItems[0].key);
  }, [sortItems]);

  /* ===== RENDER ===== */
  return (
    <React.Fragment>
      <Spin spinning={isLoading}>
        <Content maxWidth={'100%'} padding={0} className="brand_detail">
          <Row style={{ maxWidth: 2560, margin: '0 auto' }}>
            <Col x={24}>
              <Content
                maxWidth={'100%'}
                height={
                  //screenSize >= 768 ? document.body.clientWidth * 0.23 : '60vw'
                  screenSize >= 768 ? 442 : 300
                }
                padding={0}
              >
                <div
                  style={{
                    ...styles.background,
                    background: brand?.background
                      ? `url(${brand?.background}) no-repeat center / cover`
                      : '#DDDDDD',
                  }}
                >
                  <div style={styles.overlay}></div>
                  <div style={{ ...styles.backgroundInfo }}>
                    <Text
                      size={
                        screenSize >= 768 ? 64 : screenSize >= 768 ? 35 : 28
                      }
                      color={'#FFFFFF'}
                      weight={700}
                      style={{ webkitLineClamp: 'unset' }}
                    >
                      {brand?.shop_information_name}
                    </Text>

                    <Text
                      weight={500}
                      color={'#FFFFFF'}
                      style={{
                        webkitLineClamp: 'unset',
                        fontSize: screenSize >= 768 ? 16 : 14,
                      }}
                    >
                      {brand?.shop_information_content}
                    </Text>
                  </div>
                </div>
              </Content>
            </Col>
          </Row>

          <Content
            maxWidth={1820}
            padding={screenSize >= 1200 ? '100px 0px' : '80px 0'}
          >
            <Row gutter={16}>
              <Col x={0} md={1} xxl={2}></Col>
              <Col x={24} md={22} xxl={20}>
                <Row
                  gutter={16}
                  style={{ marginTop: screenSize >= 768 ? 60 : 0 }}
                >
                  <Col x={0} md={6} lg={5}>
                    {/* SECTION: 카테고리 영역 */}
                    <div style={{ borderBottom: '2px solid #111111' }}>
                      <Collapse02
                        title={'카테고리'}
                        titleStyle={{
                          border: 'none',
                          fontSize: 16,
                          fontWeight: 700,
                          padding: 0,
                          height: 52,
                          borderBottom: '2px solid #111111',
                          marginBottom: 24,
                          alignItems: 'flex-start',
                        }}
                        children={categories?.map((category, index) => (
                          <div
                            key={`category-${category.key}-${index}`}
                            style={{ ...styles.categoryWrap }}
                          >
                            <h4
                              style={{
                                ...styles.categoryTitle,
                                ...(selectedCategory?.product_category_id.toString() ===
                                  category.key && {
                                  color: '#D33444',
                                }),
                              }}
                              onClick={() => handleCategoryRoute(category.key)}
                            >
                              {category.title}
                            </h4>
                            {category.children.map((child) =>
                              renderChild(child)
                            )}
                          </div>
                        ))}
                        defaultOpened={true}
                      />
                    </div>

                    {/* SECTION: 필터 영역 */}
                    {filterItems.map((_item, _index) => (
                      <div key={`filter_${_item.title}_${_index}`}>
                        <FilterOption
                          title={_item.title}
                          options={_item.options}
                          multiple={_item.multiple}
                          value={
                            selectedFilter.find(
                              (filter) => filter.key === _item.key
                            )
                              ? selectedFilter.find(
                                  (filter) => filter.key === _item.key
                                ).value
                              : []
                          }
                          style={{
                            padding: '24px 0px',
                            borderBottom: '1px solid #DDDDDD',
                          }}
                          collapsible={true}
                          onChange={(e) =>
                            handleSelectFilter(_item.key, e, _item.type)
                          }
                        />
                      </div>
                    ))}
                    {/* SECTION: 검색 버튼 */}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '24px',
                        marginTop: 24,
                      }}
                    >
                      <Button
                        type="secondary"
                        style={{ ...styles.button }}
                        onClick={() => handleSearch('reset')}
                      >
                        초기화
                      </Button>
                      <Button
                        style={{ ...styles.button }}
                        onClick={() => handleSearch('search')}
                      >
                        검색
                      </Button>
                    </div>
                  </Col>

                  <Col
                    x={24}
                    md={18}
                    lg={19}
                    style={{
                      paddingLeft:
                        screenSize >= 1200 ? 120 : screenSize >= 768 ? 30 : 0,
                      paddingRight: screenSize >= 768 ? 8 : 0,
                    }}
                  >
                    {/* SECTION: 상품 검색 */}
                    <Content
                      maxWidth={'100%'}
                      padding={0}
                      style={{
                        paddingBottom: screenSize >= 768 ? 80 : 20,
                      }}
                    >
                      <Content
                        maxWidth={'100%'}
                        padding={0}
                        style={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          justifyContent: 'space-between',
                          borderBottom: '2px solid #111111',
                          height: screenSize >= 768 ? 52 : 'auto',
                          paddingBottom: 15,
                        }}
                      >
                        <Text
                          style={{
                            webkitLineClamp: 'unset',
                            minWidth: 'fit-content',
                            fontSize: screenSize >= 768 ? 16 : 14,
                          }}
                        >
                          총{' '}
                          <span style={{ fontWeight: 700 }}>
                            {productItems.length}
                          </span>
                          건
                        </Text>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            //flexDirection:
                            //  screenSize >= 768 ? 'unset' : 'column-reverse',
                            gap: 15,
                            rowGap: screenSize >= 768 ? 0 : 20,
                          }}
                        >
                          {/* <Tabs
                            items={sortItems}
                            fullscreen={false}
                            position={'right'}
                            layoutStyle={{ border: 'none' }}
                            style={{
                              fontSize: 16,
                              padding: 0,
                              border: 'none',
                            }}
                            textStyle={{
                              fontWeight: 400,
                              padding: 0,
                            }}
                            activeColor={'var(--black-color)'}
                            activeTextStyle={{ fontWeight: 700 }}
                            bordered={false}
                            activeKey={tabKey}
                            onChange={(k) => setTabKey(k)}
                          /> */}
                          <DropDownMenu
                            items={sortItems.map((item) => ({
                              label: item.label,
                              value: item.key,
                            }))}
                            value={tabKey}
                            onSelect={({ value }) => setTabKey(value)}
                            layoutStyle={{ paddingLeft: 10 }}
                          />
                          <DropDownMenu
                            items={PAGE_NUM}
                            value={pageSize}
                            onSelect={({ value }) => setPageSize(value)}
                          />
                          <Button
                            style={{
                              width: 45,
                              borderRadius: 0,
                              backgroundColor: 'transparent',
                              color: 'var(--black-color)',
                              fontSize: 14,
                              padding: 0,
                              display: screenSize >= 768 ? 'none' : 'flex',
                              alignItems: 'center',
                              gap: 5,
                            }}
                            onClick={() => setIsOpen(true)}
                          >
                            필터
                            <img src={Filter} alt="필터" />
                          </Button>
                        </div>
                      </Content>
                    </Content>

                    {/* SECTION: 상품목록 */}
                    <Row gutter={screenSize >= 768 ? [60, 100] : [20, 30]}>
                      {productItems.length ? (
                        productItems
                          .slice(
                            (currentPage - 1) * pageSize,
                            currentPage * pageSize
                          )
                          .map((_item, _index) => (
                            <Col
                              x={12}
                              md={12}
                              lg={8}
                              key={`product_${_index}`}
                            >
                              <ProductItem
                                key={_item.key}
                                src={_item.src}
                                title={_item.title}
                                tags={_item.tags}
                                brand={_item.brand}
                                comment={_item.comment}
                                score={_item.score}
                                price={_item.price}
                                discount={_item.discount}
                                discountType={_item.discount_type}
                                discountPrice={_item.finalPrice}
                                hovered
                                onClick={() =>
                                  navigate(
                                    `/products/${encodeURIComponent(
                                      encrypt(_item.key)
                                    )}`
                                  )
                                }
                                buttons={[
                                  {
                                    render: () =>
                                      _item.isWish ? (
                                        <HeartFilled
                                          style={{
                                            color: '#D33444',
                                            fontSize: 24,
                                          }}
                                        />
                                      ) : (
                                        <img src={Heart} alt="찜하기" />
                                      ),
                                    onClick: (event) => {
                                      handleRenderModal('wishlist', _item);
                                      event.stopPropagation();
                                    },
                                  },
                                  {
                                    render: () => (
                                      <Badge count={cartList}>
                                        <img src={Cart} alt="장바구니" />
                                      </Badge>
                                    ),
                                    onClick: (event) => {
                                      handleRenderModal('cart', _item);
                                      event.stopPropagation();
                                    },
                                  },
                                ]}
                              />
                            </Col>
                          ))
                      ) : (
                        <div
                          style={{
                            width: '100%',
                            height: 800,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <Empty
                            description={
                              <Text size={18}>
                                조건에 맞는 상품이 없습니다.
                              </Text>
                            }
                          />
                        </div>
                      )}
                    </Row>

                    {/* SECTION: 페이지네이션 */}
                    <Content
                      maxWidth={'100%'}
                      padding={0}
                      style={{ marginTop: 200 }}
                    >
                      <Pagination
                        total={productItems.length}
                        pageSize={pageSize}
                        showPage={5}
                        current={currentPage}
                        onShowSizeChange={(current, size) => {
                          setCurrentPage(1); // 페이지 크기 변경 시 페이지 초기화
                        }}
                        onChange={(page) => setCurrentPage(page)}
                        buttonStyle={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: '#999999',
                          background: 'none',
                          width: 28,
                          height: 28,
                        }}
                        isButton={true}
                        activeStyle={{
                          color: `var(--black-color) !important`,
                          borderBottom: '2px solid #111111',
                        }}
                        hoverStyle={{ color: `var(--black-color) !important` }}
                        navStyle={{ border: '1px solid var(--black-color)' }}
                        isLatestButton={true}
                      />
                    </Content>
                  </Col>
                </Row>
              </Col>
              <Col x={0} md={1} xxl={2}></Col>
            </Row>
          </Content>
        </Content>
      </Spin>

      {modalData?.data ? (
        <ProductCartModal
          type={modalData.type}
          data={modalData.data}
          onClose={() => setModalData({ type: '', data: null })}
          onOk={onSubmit}
        />
      ) : null}

      {/* 모바일 필터 */}
      <ModalLayout
        maxWidth={'100%'}
        type="drawer"
        open={isOpen}
        onChange={setIsOpen}
      >
        {/* 필터 */}
        <Content maxWidth={'100%'} padding={0}>
          {/* SECTION: 필터 영역 */}
          <div style={{ paddingBottom: 50 }}>
            {filterItems.map((_item, _index) => (
              <div key={`filter_${_item.title}_${_index}`}>
                <FilterOption
                  title={_item.title}
                  options={_item.options}
                  multiple={_item.multiple}
                  value={
                    selectedFilter.find((filter) => filter.key === _item.key)
                      ? selectedFilter.find(
                          (filter) => filter.key === _item.key
                        ).value
                      : []
                  }
                  style={{
                    padding: '24px 0px',
                    borderBottom: '1px solid #DDDDDD',
                  }}
                  collapsible={true}
                  onChange={(e) => handleSelectFilter(_item.key, e, _item.type)}
                />
              </div>
            ))}
          </div>
          {/* SECTION: 검색 버튼 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '24px',
              marginTop: 24,
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              padding: '20px 15px',
              backgroundColor: 'var(--white-color)',
              zIndex: 99999,
            }}
          >
            <Button
              type="secondary"
              style={{ ...styles.button }}
              onClick={() => {
                handleSearch('reset');
                setIsOpen(false);
              }}
            >
              초기화
            </Button>
            <Button
              style={{ ...styles.button }}
              onClick={() => {
                handleSearch('search');
                setIsOpen(false);
              }}
            >
              검색
            </Button>
          </div>
        </Content>
      </ModalLayout>
      {/* 모바일 필터 끝 */}
    </React.Fragment>
  );
};

export default BrandDetailPresenter;
